import {useCallback, useState} from 'react';
import {I18n} from 'aws-amplify/utils';

import {
    useDeclareProviderCapabilitiesMutation,
    useProviderConnectionQuery,
} from './useProviderConnectionsQuery';
import {RoleGrantKey, RoleGrantSelection} from '../services/rolePolicyBuilder';
import {
    elevatedSelection,
    isElevatable,
} from '../services/grantElevation';
import {
    clearElevationDecline,
    hasDeclinedElevation,
    recordElevationDecline,
} from '../services/elevationDeclines';
import {
    buildDeclareInput,
    declaredCertificateGrant,
    declaredDnsGrant,
    hasCertificateDeclaration,
    ProviderConnection,
} from '../types';
import {
    showErrorNotifications,
    showSuccessNotification,
} from '../../../components/ServiceNotifications';

/**
 * FNP-1365 — SHOULD WE ASK, AND WHAT HAPPENS WHEN THEY SAY YES.
 *
 * Everything stateful about an elevation lives here so the delete affordances
 * stay what they are: a zone modal and a certificate modal, each with one extra
 * branch. Putting the decline bookkeeping and the re-declaration into those
 * components would be the same logic written twice, in the two places most
 * likely to drift.
 *
 * ⚠️ THE ANSWER COMES FROM THE STORED DECLARATION, BEFORE ANY ATTEMPT.
 *
 * This is the ticket's first requirement and the difference between an
 * invitation and an error. The declaration is already on the connection, so
 * whether a delete can succeed is knowable without calling the provider and
 * watching it fail. A prompt shown BEFORE the attempt is an offer; the same
 * words after a failure are an apology.
 *
 * ⚠️ READ THROUGH `declaredDnsGrant`, WHICH FAILS CLOSED. An absent block, an
 * absent field, a null and an explicit false all mean not granted. That is the
 * right direction here too: a connection whose declaration cannot be read is
 * one we should ask about rather than one we should assume can delete.
 */

/**
 * The current declaration as a grant selection.
 *
 * ⚠️ FNP-1398 — THE CERTIFICATE GRANTS NOW SEED FROM THE STORED DECLARATION.
 * They used to be hardcoded false because nothing typed `certificateCapabilities`
 * (the queries always selected it), and this function's own note explained that
 * `buildDeclareInput` omitting the certificate block was LOAD-BEARING as a
 * result: an elevation sends a correction, a correction states the complete
 * intended state, and a block built from invented falses would REVOKE the
 * tenant's real ACM grants every time they widened a DNS one.
 *
 * That hazard has not gone away — it has moved to where it can be checked.
 * Reading the real values removes the invention, and `buildDeclareInput` now
 * takes an explicit readability argument, so a connection whose block never
 * arrived still sends nothing at all rather than two falses. Both halves are
 * needed: seeding honestly without the gate would revoke on a partial response,
 * and the gate without honest seeding would send a stale false.
 */
export const declaredSelection = (connection: ProviderConnection): RoleGrantSelection => ({
    readDns: true,
    writeRecords: declaredDnsGrant(connection, 'canWriteRecords'),
    createZones: declaredDnsGrant(connection, 'canCreateZones'),
    deleteZones: declaredDnsGrant(connection, 'canDeleteZones'),
    requestCertificates: declaredCertificateGrant(connection, 'canRequest'),
    deleteCertificates: declaredCertificateGrant(connection, 'canDelete'),
});

export const useGrantElevation = (
    connectionId: string | undefined,
    grant: RoleGrantKey,
) => {

    const {data: connection} = useProviderConnectionQuery(connectionId);
    const declareMutation = useDeclareProviderCapabilitiesMutation();

    /**
     * ⚠️ THE DECLINE IS READ ONCE INTO STATE, NOT ON EVERY RENDER.
     *
     * Reading storage during render would make the component's output depend on
     * something React cannot see change, and the prompt would not disappear
     * when the customer declines until something else re-rendered it. Seeding
     * state from storage and updating both together keeps the two in step.
     */
    const [declined, setDeclined] = useState<boolean>(() =>
        connectionId ? hasDeclinedElevation(connectionId, grant) : false,
    );

    // Whether the connection already declares this grant. Unknown connection
    // means unknown declaration, and an unknown declaration is not a grant.
    const granted = connection
        ? declaredSelection(connection)[grant]
        : false;

    /**
     * ⚠️ ONLY DESTRUCTIVE GRANTS ARE ASKED FOR AFTER THE FACT (open question 2,
     * decided in `grantElevation.ts`). A caller passing `writeRecords` gets no
     * prompt rather than a surprise upsell mid-task.
     *
     * The four conditions, in the order they answer:
     *   · this grant is one we elevate at all
     *   · we know which connection to ask about
     *   · they have not already granted it
     *   · they have not already said no
     */
    const shouldPrompt = isElevatable(grant)
        && Boolean(connectionId)
        && Boolean(connection)
        && !granted
        && !declined;

    const decline = useCallback(() => {
        if (connectionId) {
            recordElevationDecline(connectionId, grant);
        }
        setDeclined(true);
    }, [connectionId, grant]);

    /**
     * The customer says they have widened the role. Re-declare.
     *
     * ⚠️ THROUGH `declareProviderCapabilities`, NEVER RE-VALIDATE. Re-validate
     * re-checks whether the credential still works and deliberately leaves the
     * declaration alone; running it here would report a cheerful success and
     * change nothing at all, which is the silent no-op this feature is most
     * likely to ship as.
     *
     * ⚠️ AND THE SELECTION IS SEEDED FROM WHAT IS STORED. `elevatedSelection`
     * takes the current declaration and adds one grant, because a correction
     * states the COMPLETE intended state — anything missing from it is sent as
     * a revocation. Granting delete must not silently cost them record writes.
     */
    const grantIt = useCallback(async () => {
        if (!connection || !connectionId) {
            return;
        }
        try {
            await declareMutation.mutateAsync(
                buildDeclareInput(
                    connectionId,
                    elevatedSelection(declaredSelection(connection), grant),
                    // ⚠️ FNP-1398 — THE GATE, NOT A CONSTANT. This elevation is
                    // about a DNS grant, so it never intends to touch the
                    // certificate block; passing the readability flag means the
                    // block travels only when it was seeded from real stored
                    // values, and is omitted — the API's "keep what is stored" —
                    // whenever it was not.
                    hasCertificateDeclaration(connection),
                ),
            );

            // A grant supersedes any earlier decline. If they later tighten the
            // role again, they should be asked afresh rather than silenced by a
            // decision they have since reversed.
            clearElevationDecline(connectionId, grant);
            setDeclined(false);

            showSuccessNotification(I18n.get('Permission recorded. You can try that again now.'));

        } catch (e) {
            // ⚠️ NOT RETHROWN. This handler is called straight from an onClick
            // with nobody to catch it, so a rethrow becomes an unhandled
            // rejection that fails the test run while every assertion passes —
            // the same trap `DeclareCapabilitiesDrawer` documents. The prompt
            // stays open on failure so a retry is one click.
            showErrorNotifications(e);
        }
    }, [connection, connectionId, declareMutation, grant]);

    return {
        /** Render the prompt instead of the action. */
        shouldPrompt,
        /** The connection's declaration today — what the offered policy builds on. */
        currentSelection: connection ? declaredSelection(connection) : undefined,
        /** True once the grant is declared, which is the caller's cue to re-offer the action. */
        granted,
        declined,
        decline,
        grantIt,
        isSaving: declareMutation.isPending,
    };
};
